import { lazy, type ComponentType } from 'react'

// After a deploy, the app's code-split chunks get new content-hashed filenames and
// the old ones are removed from the server. A browser that loaded the app before the
// deploy still references the OLD chunk names, so navigating to a lazy route fails
// with "Failed to fetch dynamically imported module" (a 404). The fix: when a lazy
// import fails, reload the page ONCE to pull the fresh index.html + chunk names.
//
// Guarded by a timestamp in sessionStorage so a genuinely broken chunk (not a stale
// one) can't cause an infinite reload loop — we only auto-reload if we haven't
// already done so in the last few seconds; otherwise the error surfaces normally.
const RELOAD_KEY = 'chunkReloadAt'
const RELOAD_WINDOW_MS = 10_000

export function lazyWithRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
) {
  return lazy(() =>
    factory().catch((err: unknown) => {
      let last = 0
      try {
        last = Number(sessionStorage.getItem(RELOAD_KEY) || 0)
      } catch {
        // sessionStorage unavailable (private mode) — fall through to rethrow.
      }
      const now = Date.now()
      if (now - last > RELOAD_WINDOW_MS) {
        try {
          sessionStorage.setItem(RELOAD_KEY, String(now))
        } catch {
          // ignore
        }
        window.location.reload()
        // Never resolve, so React doesn't flash the error boundary before reload.
        return new Promise<{ default: T }>(() => {})
      }
      // We already reloaded recently and it still failed — this is a real error.
      throw err
    }),
  )
}
